import { useTranslation } from 'react-i18next'
import { AccountNumberDto, useAddUserAccountNumber, useDeleteUserAccountNumber } from '@dhl/mdp-api'
import { Body2, Box, ListItem, Checkbox, List, ListItemSelect, ListItemText } from '@dhl-ui/components'

import { useOrganisatonAccounts, useUserAccountNumbers } from '~/common/components/accountnumbers'
import { useIsLoading } from '~/common/hooks'
import { isBusinessUser, type User } from '~/modules/users/model'

type Props = {
  user: User
}

export const EditUserOrganisationAccounts = ({ user }: Props) => {
  const { id, userType, organisationId } = user

  const { t } = useTranslation()
  const { isLoading: isSaving, setIsLoading: setIsSaving } = useIsLoading()

  const {
    userAccountNumbers,
    isLoading: isLoadingUserAccountNumbers,
    refetch: refetchUserAccountNumbers,
  } = useUserAccountNumbers(userType, id)

  const { mutateAsync: addUserAccountNumber } = useAddUserAccountNumber()
  const { mutateAsync: deleteUserAccountNumber } = useDeleteUserAccountNumber()
  const { accountDtos, isFetching: isFetchingOrganisationAccounts } = useOrganisatonAccounts(
    userType,
    organisationId,
    userType === 'terminal' ? 'terminal' : 'organisation'
  )

  const updateAccountNumber = async (accountDto: AccountNumberDto, mode: 'add' | 'delete') => {
    setIsSaving(true)
    const mutateOptions = {
      onSuccess: () => {
        setIsSaving(false)
        refetchUserAccountNumbers()
      },
      onError: (error) => {
        setIsSaving(false)
        console.error('Error adding account number:', error)
      },
    }
    if (mode === 'add') {
      addUserAccountNumber(
        {
          emailOrId: id,
          data: accountDto,
        },
        mutateOptions
      )
    } else {
      deleteUserAccountNumber(
        {
          emailOrId: id,
          accountNumber: accountDto.id,
        },
        mutateOptions
      )
    }
  }

  return organisationId && isBusinessUser(userType) ? (
    <Box>
      {accountDtos.length > 0 && (
        <>
          <Box marginTop={2}>
            <Body2 variant="tertiary">{t('admin.usersPage.organisationDetailsView.accountNumber')}</Body2>
          </Box>
          <List>
            {accountDtos.map((account, index) => {
              const orgAccountNumber = account.id
              const checked = userAccountNumbers.includes(orgAccountNumber)
              const isDisabled = isLoadingUserAccountNumbers || isFetchingOrganisationAccounts || isSaving
              return (
                <ListItem
                  isButton
                  key={`accountNumber_${index}`}
                  data-testid="accountNumber_list"
                  onClick={() => updateAccountNumber(account, checked ? 'delete' : 'add')}
                  hasDivider={false}
                  alignItems="center"
                  isDisabled={isDisabled}
                >
                  <ListItemSelect>
                    <Checkbox data-testid="accountNumber_check" checked={checked} disabled={isDisabled} />
                  </ListItemSelect>
                  <ListItemText primary={orgAccountNumber} />
                </ListItem>
              )
            })}
          </List>
        </>
      )}
    </Box>
  ) : null
}
